import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { RoleName } from '../lib/types';
import { setSentryUser, clearSentryUser } from '../lib/sentry';

interface AuthState {
  token: string | null;
  userId: string | null;
  userName: string | null;
  role: RoleName;
  theme: 'light' | 'dark';
  learningMode: boolean;
  setAuth: (token: string, userId: string, userName: string, role: RoleName) => void;
  logout: () => void;
  setTheme: (theme: 'light' | 'dark') => void;
  toggleTheme: () => void;
  setLearningMode: (on: boolean) => void;
  setRole: (role: RoleName) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      userId: null,
      userName: null,
      role: 'glenda',
      theme: 'light',
      learningMode: false,

      setAuth: (token, userId, userName, role) => {
        set({ token, userId, userName, role });
        // 3.9s — tag every subsequent Sentry event with the active
        // user_id + role + ranch_id so triage is one click in the
        // dashboard instead of cross-referencing screenshots.
        setSentryUser(userId, role);
      },

      logout: () => {
        // Sprint 3.6 — clear vaquero local state on logout. Without this,
        // the next user to log in on the same phone (or the same user
        // after an admin reset of their bitacora) sees stale counter
        // badges and a stale cronómetro start time. Keys live in
        // OrdenoHoyMain.tsx (STORAGE_KEY / SESSION_KEY).
        try {
          localStorage.removeItem('manga-vaquero-counter');
          localStorage.removeItem('manga-vaquero-session-start');
        } catch {
          // localStorage can throw in private mode / quota exhausted —
          // ignore, the auth wipe below is what actually matters.
        }
        set({ token: null, userId: null, userName: null, role: 'glenda' });
        // 3.9s — drop Sentry user context too; the next user on this
        // device should not inherit the previous user's tagging.
        clearSentryUser();
      },

      setTheme: (theme) => {
        document.documentElement.setAttribute('data-theme', theme);
        set({ theme });
      },

      toggleTheme: () => {
        const next = get().theme === 'light' ? 'dark' : 'light';
        document.documentElement.setAttribute('data-theme', next);
        set({ theme: next });
      },

      setLearningMode: (on) => set({ learningMode: on }),

      setRole: (role) => set({ role }),
    }),
    {
      name: 'manga-auth',
      partialize: (state) => ({
        token: state.token,
        userId: state.userId,
        userName: state.userName,
        role: state.role,
        theme: state.theme,
        learningMode: state.learningMode,
      }),
      // 3.9s — when the persisted store rehydrates on a page load, the
      // stored user is technically already logged in. Replay setSentryUser
      // so we don't have an anonymous window between mount and the user's
      // next interaction.
      onRehydrateStorage: () => (state) => {
        if (state?.userId) {
          setSentryUser(state.userId, state.role);
        }
      },
    },
  ),
);
